import React, { useMemo } from 'react';
import { Box, Card, Flex, Text, Tooltip } from '@mantine/core';
import { IconBuilding, IconFlag2, IconUsers } from '@tabler/icons';
import type { RelatedIdeaByEvidence } from '../../../api/discoveryAgent';

const BAR_COUNT = 5;
const BAR_FILLED_COLOR = '#0BAC4D';
const BAR_EMPTY_COLOR = '#D8D8DB';
const LABEL_COLOR = '#585C68';
const VALUE_COLOR = '#212529';
const BORDER_COLOR = '#D8D8DB';

function EvidenceMatchBars({
  matchCount,
  totalEvidence,
}: {
  matchCount: number;
  totalEvidence: number;
}) {
  const filledBars = useMemo(() => {
    if (totalEvidence <= 0) {
      return 0;
    }
    const ratio = matchCount / totalEvidence;
    return Math.min(BAR_COUNT, Math.max(0, Math.round(ratio * BAR_COUNT)));
  }, [matchCount, totalEvidence]);

  return (
    <Flex align="flex-end" gap={2} sx={{ height: 23, width: 48, flexShrink: 0 }}>
      {Array.from({ length: BAR_COUNT }).map((_, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            height: `${Math.round(33 + i * 16.7)}%`,
            borderRadius: 1,
            backgroundColor: i < filledBars ? BAR_FILLED_COLOR : BAR_EMPTY_COLOR,
          }}
        />
      ))}
    </Flex>
  );
}

export interface RelatedIdeaCardProps {
  idea: RelatedIdeaByEvidence;
}

export const RelatedIdeaCard: React.FC<RelatedIdeaCardProps> = ({ idea }) => {
  const totalEvidence = idea.ideaEvidenceTotal ?? 0;
  const companies = idea.aggregations?.accountsCount?.total ?? 0;

  const handleClick = () => {
    window.open(`/idea/${idea._id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card
      padding={12}
      radius={12}
      withBorder
      sx={{
        width: 288,
        minWidth: 220,
        flexShrink: 0,
        cursor: 'pointer',
        borderColor: BORDER_COLOR,
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        transition: 'border-color 150ms ease, background-color 150ms ease',
        '&:hover': {
          borderColor: '#5C5CEB',
          backgroundColor: '#F8F9FA',
        },
      }}
      onClick={handleClick}
    >
      <Text fw={500} c={VALUE_COLOR} sx={{ fontSize: 14, lineHeight: '24px' }} lineClamp={2}>
        {idea.title || '—'}
      </Text>

      <Box sx={{ flex: 1 }} />


      <Box sx={{ borderBottom: `1px solid ${BORDER_COLOR}`, paddingBottom: 8 }}>
        <Tooltip
          label={`${idea.matchCount} of the evidences are matched or assigned to this idea`}
          withArrow
          withinPortal
          multiline
          maw="250px"
          position="top-start"
        >
          <Flex direction="column" gap={6}>
            <Flex align="flex-end" gap={8}>
              <EvidenceMatchBars matchCount={idea.matchCount} totalEvidence={totalEvidence} />
              <Flex align="flex-end" gap={3}>
                <Text fw={600} c={BAR_FILLED_COLOR} sx={{ fontSize: 20, lineHeight: '20px' }}>
                  {idea.matchCount}
                </Text>
                <Text fw={400} c={VALUE_COLOR} sx={{ fontSize: 12, lineHeight: '15px' }}>
                  / {totalEvidence}
                </Text>
              </Flex>
            </Flex>
            <Text c={LABEL_COLOR} sx={{ fontSize: 9.5, lineHeight: '12px' }}>
              Evidence match
            </Text>
          </Flex>
        </Tooltip>
      </Box>

      <Flex gap={12} align="flex-end">
        <Box sx={{ padding: 2 }}>
          <Flex align="center" gap={2} mb={2}>
            <IconUsers size={16} color={LABEL_COLOR} />
            <Text c={LABEL_COLOR} sx={{ fontSize: 10, lineHeight: '16px' }}>
              Owner
            </Text>
          </Flex>
          <Text c={VALUE_COLOR} sx={{ fontSize: 10, lineHeight: '12px' }} lineClamp={1}>
            {idea.ownerName || '—'}
          </Text>
        </Box>
        <Box sx={{ padding: 2 }}>
          <Flex align="center" gap={2} mb={2}>
            <IconBuilding size={16} color={LABEL_COLOR} />
            <Text c={LABEL_COLOR} sx={{ fontSize: 10, lineHeight: '16px' }}>
              Companies
            </Text>
          </Flex>
          <Text c={VALUE_COLOR} sx={{ fontSize: 10, lineHeight: '12px' }}>
            {companies}
          </Text>
        </Box>
        <Box sx={{ padding: 2 }}>
          <Flex align="center" gap={2} mb={2}>
            <IconFlag2 size={16} color={LABEL_COLOR} />
            <Text c={LABEL_COLOR} sx={{ fontSize: 10, lineHeight: '16px' }}>
              Status
            </Text>
          </Flex>
          <Text c={VALUE_COLOR} sx={{ fontSize: 10, lineHeight: '12px' }} lineClamp={1}>
            {idea.status || '—'}
          </Text>
        </Box>
      </Flex>
    </Card>
  );
};

export default RelatedIdeaCard;
